import { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import {
  Lightbulb,
  DollarSign,
  FileText,
  BarChart3,
  Target,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Rocket,
  Settings,
  Users,
  Megaphone,
} from "lucide-react";
import { INPUT_COLOR_BORDER } from "../../utils/colors";

const QUICK_REPLY_ICONS = {
  idea: Lightbulb,
  budget: DollarSign,
  copy: FileText,
  analytics: BarChart3,
  target: Target,
  trend: TrendingUp,
  warning: AlertTriangle,
  check: CheckCircle2,
  rocket: Rocket,
  settings: Settings,
  audience: Users,
  ads: Megaphone,
};

const QuickRepliesChips = ({
  onClickChip = () => {},
  replies = [],
  isLoading = false,
}) => {
  const scrollRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startScrollLeftRef = useRef(0);
  const hasDraggedRef = useRef(false);
  const suppressClickRef = useRef(false);
  const quickReplies = replies;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || (quickReplies.length === 0 && !isLoading)) return undefined;

    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY + e.deltaX;
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [quickReplies.length, isLoading]);

  const onPointerDownDrag = (event) => {
    const el = scrollRef.current;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;

    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    startXRef.current = event.clientX;
    startScrollLeftRef.current = el.scrollLeft;
  };

  const onPointerMoveDrag = (event) => {
    const el = scrollRef.current;
    if (!el || !isDraggingRef.current) return;

    const deltaX = event.clientX - startXRef.current;
    const nextScrollLeft = startScrollLeftRef.current - deltaX;
    if (
      Math.abs(deltaX) > 8 &&
      Math.abs(nextScrollLeft - startScrollLeftRef.current) > 2
    ) {
      hasDraggedRef.current = true;
    }
    el.scrollLeft = nextScrollLeft;
  };

  const onPointerEndDrag = () => {
    const el = scrollRef.current;
    if (!el || !isDraggingRef.current) return;

    isDraggingRef.current = false;
    if (hasDraggedRef.current) {
      suppressClickRef.current = true;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 80);
    }
  };

  if (quickReplies.length === 0 && !isLoading) return null;

  return (
    <Box
      ref={scrollRef}
      onPointerDown={onPointerDownDrag}
      onPointerMove={onPointerMoveDrag}
      onPointerUp={onPointerEndDrag}
      onPointerLeave={onPointerEndDrag}
      onPointerCancel={onPointerEndDrag}
      sx={{
        py: 1,
        display: "flex",
        gap: 1,
        overflowX: "auto",
        overflowY: "hidden",
        flexWrap: "nowrap",
        whiteSpace: "nowrap",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        touchAction: "pan-y",
        cursor: "grab",
        "&:active": {
          cursor: "grabbing",
        },
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      {isLoading
        ? Array.from({ length: 3 }).map((_, index) => (
            <Skeleton
              // eslint-disable-next-line react/no-array-index-key
              key={`quick-reply-skeleton-${index}`}
              variant="rounded"
              height={32}
              sx={{
                borderRadius: 16,
                flexShrink: 0,
                minWidth: 130,
              }}
            />
          ))
        : quickReplies.map((quickReply) => {
            const Icon =
              QUICK_REPLY_ICONS[quickReply.icon] || QUICK_REPLY_ICONS.idea;
            return (
              <Chip
                key={quickReply.id}
                icon={<Icon size={14} />}
                label={quickReply.title}
                onClick={() => {
                  if (suppressClickRef.current) return;
                  onClickChip(quickReply.message);
                }}
                sx={{
                  backgroundColor: "fields.background",
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  flexShrink: 0,
                  minWidth: 50,
                  opacity: 1,
                  px: 1,
                  "&:hover": {
                    backgroundColor: "fields.background",
                  },
                  "& .MuiChip-icon": {
                    color: "text.primary",
                  },
                }}
              />
            );
          })}
    </Box>
  );
};

export default QuickRepliesChips;
